import { useEffect, useRef, useState } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  ArrowLeft,
  ArrowRight,
  BrainCircuit,
  Check,
  FileUp,
  FolderOpen,
  LoaderCircle,
  LockKeyhole,
  ScanLine,
} from 'lucide-react';
import type {
  LegacyImportStatus,
  LegacyInstallation,
  LegacyJobSummary,
} from '../shared/desktop-api';
import { WorkspaceDialog } from './WorkspaceDialog';
import { hostHas } from '../shared/host-capabilities';
import { messageFor } from '../shared/customer-message';
import './job-import.css';

export function JobImportDialog({
  onClose,
  onFile,
  onImported,
  startWithLegacy = false,
  resume,
}: {
  onClose: () => void;
  onFile: () => Promise<void>;
  onImported: (result: LegacyImportStatus) => void;
  /** The welcome's "Bring your jobs from earlier Paige": open straight on the earlier jobs. */
  startWithLegacy?: boolean;
  /** The job menu's Finish import: open straight on that unfinished import. */
  resume?: LegacyImportStatus;
}) {
  const [source, setSource] = useState<'choose' | 'legacy'>(
    startWithLegacy || resume ? 'legacy' : 'choose',
  );
  const [installation, setInstallation] = useState<LegacyInstallation | null>(null);
  const [selected, setSelected] = useState<LegacyJobSummary | null>(null);
  const [recent, setRecent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LegacyImportStatus | null>(resume ?? null);
  const importId = useRef<string | null>(null);
  const [reading, setReading] = useState(false);
  // Set once a reading's own request has answered: a status poll still in flight
  // then must not put "Import paused" back over "Your job is ready".
  const readingSettled = useRef(false);
  const [unfinished, setUnfinished] = useState<LegacyImportStatus[]>([]);
  // One file picker at a time: further clicks while it is open, or while the chosen setup
  // imports, do nothing (owner, 2026-10-10: repeated clicks opened several pickers).
  const fileOpening = useRef(false);
  const [openingFile, setOpeningFile] = useState(false);
  // Saved job setup: from the moment the file is chosen until the job is made.
  const [fromFile, setFromFile] = useState(false);
  useEffect(
    () =>
      window.capturePointDesktop?.onJobSetupFileWorking?.((work) => {
        if (work.action === 'import') setFromFile(true);
      }),
    [],
  );
  const openFile = async () => {
    if (fileOpening.current) return;
    fileOpening.current = true;
    setOpeningFile(true);
    setError(null);
    try {
      await onFile();
    } catch (reason) {
      // PAIGE (shared/customer-message.ts): never a runtime's own sentence in front of a person.
      setError(messageFor(reason, 'The job setup could not be imported.'));
    } finally {
      fileOpening.current = false;
      setOpeningFile(false);
      setFromFile(false);
    }
  };
  useEffect(() => {
    let closed = false;
    void window
      .capturePointDesktop!.listLegacyImports()
      .then((items) => {
        if (!closed) setUnfinished(items);
      })
      .catch((reason) => {
        if (!closed)
          setError(
            reason instanceof Error ? reason.message : 'Unfinished imports could not be checked.',
          );
      });
    return () => {
      closed = true;
    };
  }, []);
  useEffect(() => {
    if (!reading || !result) return;
    let closed = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const status = await window.capturePointDesktop!.legacyImportStatus(result.job.id);
        if (!closed && !readingSettled.current) setResult(status);
      } catch {
        /* The owning request reports failures; a status miss must not restart work. */
      }
      if (!closed) timer = setTimeout(() => void poll(), 1500);
    };
    void poll();
    return () => {
      closed = true;
      clearTimeout(timer);
    };
  }, [reading, result?.job.id]);
  const learn = async (imported: LegacyImportStatus) => {
    readingSettled.current = false;
    setBusy(true);
    setReading(true);
    setError(null);
    try {
      if (imported.state === 'copying' || imported.state === 'copy-failed') {
        imported = await window.capturePointDesktop!.importLegacyJob({
          sourceJobId: imported.sourceJobId,
          importId: imported.job.id,
          recentPages: imported.requestedRecentPages,
          connectionSource: imported.connectionSource,
        });
        setResult(imported);
      }
      const finished = await window.capturePointDesktop!.analyzeLegacyImport(imported.job.id);
      readingSettled.current = true;
      setResult(finished);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Import stopped. Your progress is saved.',
      );
    } finally {
      readingSettled.current = true;
      setReading(false);
      setBusy(false);
    }
  };
  const load = async (connectionSource?: string) => {
    setSource('legacy');
    setBusy(true);
    setError(null);
    try {
      setInstallation(await window.capturePointDesktop!.listLegacyJobs(connectionSource));
      setSelected(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The earlier setup could not be read.');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (startWithLegacy) void load();
    // Once, on open: later choices are the person's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const run = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    importId.current ??= crypto.randomUUID();
    try {
      const imported = await window.capturePointDesktop!.importLegacyJob({
        sourceJobId: selected.id,
        importId: importId.current,
        recentPages: recent ? Math.min(selected.pageCount, 1000) : 0,
        connectionSource: installation?.connectionSource,
      });
      setResult(imported);
      await learn(imported);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The setup could not be imported.');
    } finally {
      setBusy(false);
    }
  };
  const principles = (
    <div className="job-import-principles">
      <section>
        <LockKeyhole />
        <strong>Your familiar setup</strong>
        <span>Keep your document types and fields</span>
      </section>
      <section>
        <BrainCircuit />
        <strong>A head start</strong>
        <span>Prepare using your existing examples</span>
      </section>
      <section>
        <ScanLine />
        <strong>Ready for new scans</strong>
        <span>No old documents to export again</span>
      </section>
    </div>
  );
  // Its setup is copied once the job is active; only the reading of earlier examples may be left.
  const usable = result?.job.status === 'active';
  // Closing a finished import opens its job, like Start using this job (owner, 2026-10-10: staying
  // on the previous job's documents made them look like the new job's).
  return (
    <WorkspaceDialog
      telemetry="job-import"
      title="Import job setup"
      onClose={() => {
        if (busy || fromFile) return;
        if (result?.state === 'complete') onImported(result);
        else onClose();
      }}
    >
      <div className={`job-import${result ? ' job-import-outcome' : ''}`}>
        {source === 'choose' ? (
          <>
            <p className="job-import-lead">Choose where to import your job from</p>
            <div className="job-import-sources">
              <button
                className="job-import-source"
                disabled={openingFile}
                aria-busy={openingFile}
                onClick={() => void openFile()}
              >
                <FileUp size={34} />
                <strong>Saved job setup</strong>
                <span>
                  Includes settings and learning, but no scanned documents. Creates a new job.
                </span>
                {openingFile ? (
                  <LoaderCircle className="job-import-spinner" size={22} />
                ) : (
                  <ArrowRight size={22} />
                )}
              </button>
              {/* PAIGE (docs/engineering/workbench-port.md): the earlier application is on a
                  workstation's disk, which Paige cannot read. */}
              {hostHas('legacy-import') && (
                <button
                  className="job-import-source"
                  disabled={openingFile}
                  onClick={() => void load()}
                >
                  <FolderOpen size={34} />
                  <strong>Legacy Paige</strong>
                  <span>Bring a job from the earlier application.</span>
                  <ArrowRight size={22} />
                </button>
              )}
            </div>
            {fromFile && (
              <p className="job-import-progress" role="status">
                <LoaderCircle className="job-import-spinner" size={22} />
                Creating your job from the saved setup…
              </p>
            )}
            {unfinished.length > 0 && (
              <section className="job-import-jobs" aria-label="Unfinished imports">
                <h3>Continue an import</h3>
                {unfinished.map((item) => (
                  <button
                    key={item.job.id}
                    className="job-import-job"
                    onClick={() => {
                      setSource('legacy');
                      setResult(item);
                      setError(null);
                    }}
                  >
                    <BrainCircuit size={24} />
                    <span>
                      <strong>{item.job.name}</strong>
                      <small>Import progress saved</small>
                    </span>
                    <ArrowRight size={20} />
                  </button>
                ))}
              </section>
            )}
            {error && (
              <p role="alert" data-telemetry="error:job-import-failed">
                {error}
              </p>
            )}
          </>
        ) : result ? (
          <>
            <div className="job-import-success">
              {result.state === 'complete' ? <Check size={36} /> : <BrainCircuit size={36} />}
              <div>
                <h2>{result.state === 'complete' ? 'Your job is ready' : 'Preparing your job'}</h2>
                <p>{result.job.name}</p>
              </div>
            </div>
            <div className="job-import-reading" role="status">
              {reading && <LoaderCircle className="job-import-spinner" size={24} />}
              <strong>
                {result.state === 'complete'
                  ? 'Setup imported'
                  : reading
                    ? 'Getting to know your documents'
                    : 'Import paused'}
              </strong>
              {result.state !== 'complete' && (
                <progress
                  max={Math.max(1, result.trainingImages + result.recentImages)}
                  value={result.analyzedImages}
                  aria-label="Import preparation"
                />
              )}
              <span>
                {reading
                  ? 'Using your existing examples to prepare your job. This takes a little longer than copying settings.'
                  : result.state === 'complete'
                    ? 'Your document types and fields are in place. Start scanning-your confirmations and corrections help Paige get smarter and process faster.'
                    : usable
                      ? 'Your progress is saved. To finish later, open the job and click its name.'
                      : 'Your progress is saved. Continue whenever you’re ready.'}
              </span>
            </div>
            {(error || result.error) && (
              <p role="alert" data-telemetry="error:job-import-failed">
                {error || result.error}
              </p>
            )}
            {result.state === 'complete' && (
              <p className="job-import-note">
                <FolderOpen size={18} /> Choose where new documents go in Export when you’re ready.
              </p>
            )}
            {result.warnings
              .filter(
                (note) =>
                  !note.startsWith('Export destinations, schedules') &&
                  !note.startsWith('Choose where new documents'),
              )
              .map((note) => (
                <p className="job-import-note" key={note}>
                  {note}
                </p>
              ))}
            <footer>
              {reading ? (
                <button
                  className="quiet-button"
                  disabled={result.state === 'pause-requested'}
                  onClick={() => {
                    void window
                      .capturePointDesktop!.pauseLegacyImport(result.job.id)
                      .then(setResult)
                      .catch((reason) =>
                        setError(
                          reason instanceof Error ? reason.message : 'Could not pause reading.',
                        ),
                      );
                  }}
                >
                  {result.state === 'pause-requested' ? 'Saving progress…' : 'Finish later'}
                </button>
              ) : (
                result.state !== 'complete' && (
                  <button className="primary-button" onClick={() => void learn(result)}>
                    Continue import
                  </button>
                )
              )}
              {result.state === 'complete' ? (
                <>
                  <span className="job-import-note">Your legacy job is unchanged.</span>
                  <button className="primary-button" onClick={() => onImported(result)}>
                    Start using this job <ArrowRight size={18} />
                  </button>
                </>
              ) : (
                !busy &&
                (usable ? (
                  <button className="secondary-button" onClick={() => onImported(result)}>
                    Start using this job <ArrowRight size={18} />
                  </button>
                ) : (
                  <button className="secondary-button" onClick={onClose}>
                    Close
                  </button>
                ))
              )}
            </footer>
          </>
        ) : (
          <>
            <button
              className="quiet-button"
              disabled={busy || !!importId.current}
              onClick={() => {
                setSource('choose');
                setError(null);
              }}
            >
              <ArrowLeft size={17} /> Back
            </button>
            <p className="job-import-lead">
              {installation?.connections?.length
                ? 'Which legacy setup do you use?'
                : 'Choose the job to bring forward'}
            </p>
            {!!installation?.connections?.length && (
              <div className="job-import-jobs" aria-label="Saved legacy connections">
                {installation.connections.map((connection) => (
                  <button
                    key={connection.id}
                    className="job-import-job"
                    disabled={busy}
                    onClick={() => void load(connection.id)}
                  >
                    <FolderOpen size={23} />
                    <span>
                      <strong>{connection.databaseName}</strong>
                      <small>
                        {connection.label} · {connection.server}
                      </small>
                    </span>
                    <ArrowRight size={20} />
                  </button>
                ))}
              </div>
            )}
            {installation && <p className="job-import-note">{installation.databaseName}</p>}
            {busy && (
              <p className="job-import-progress" role="status">
                <LoaderCircle className="job-import-spinner" size={22} />
                {selected ? 'Bringing your setup into Paige…' : 'Finding your legacy jobs…'}
              </p>
            )}
            {error && (
              <div
                className="job-import-error"
                role="alert"
                data-telemetry="error:job-import-failed"
              >
                <p>{error}</p>
                <button
                  className="secondary-button"
                  disabled={busy}
                  onClick={() => void (importId.current ? run() : load())}
                >
                  Try again
                </button>
              </div>
            )}
            {installation && (
              <div className="job-import-jobs" role="group" aria-label="Earlier jobs">
                {installation.jobs.length === 0 && !installation.connections?.length && (
                  <p>This earlier database has no jobs.</p>
                )}
                {installation.jobs.map((job) => (
                  <button
                    key={job.id}
                    className={`job-import-job${selected?.id === job.id ? ' is-selected' : ''}`}
                    aria-pressed={selected?.id === job.id}
                    disabled={busy || !!importId.current}
                    onClick={() => {
                      setSelected(job);
                      setError(null);
                    }}
                  >
                    <FolderOpen size={23} />
                    <span>
                      <strong>{job.name}</strong>
                      <small>
                        {job.documentTypes.length} types ·{' '}
                        {job.documentTypes.reduce((sum, type) => sum + type.fields.length, 0)}{' '}
                        fields
                      </small>
                    </span>
                    {selected?.id === job.id && <Check size={22} />}
                  </button>
                ))}
              </div>
            )}
            {selected && (
              <>
                <div className="job-import-types">
                  {selected.documentTypes.map((type) => (
                    <details key={type.id}>
                      <summary>
                        {type.name}
                        <span>{type.fields.length} fields</span>
                      </summary>
                      <p>{type.fields.length ? type.fields.join(' · ') : 'No fields configured'}</p>
                    </details>
                  ))}
                </div>
                {principles}
                <label className="job-import-recent">
                  <input
                    type="checkbox"
                    checked={recent}
                    disabled={busy || !!importId.current}
                    onChange={(event) => setRecent(event.target.checked)}
                  />
                  <span>
                    Include recent examples
                    <small>
                      Up to {Math.min(selected.pageCount, 1000)} pages, plus saved training images.
                      Old extracted answers stay behind.
                    </small>
                  </span>
                </label>
                <footer>
                  <span className="job-import-note">The earlier job stays untouched.</span>
                  <button
                    className="primary-button"
                    disabled={busy || !!error}
                    onClick={() => void run()}
                  >
                    <FolderOpen size={19} /> Import job
                  </button>
                </footer>
              </>
            )}
          </>
        )}
      </div>
    </WorkspaceDialog>
  );
}
